import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { registerModalBackHandler } from '../../lib/native';
import { AlmaBriefing } from '../design-system';
import { useAlmaAvatar } from '../../hooks/useAlmaAvatar';

/**
 * Pipeline stage a trip sits in on the host dashboard.
 * Mirrors the kanban columns: inquiry → building → confirmed → active → invoice → past.
 * `invoice` is a finished trip the host can be (or is being) paid for.
 */
export type TripStage = 'inquiry' | 'building' | 'confirmed' | 'active' | 'invoice' | 'past';

/** Handbook sub-state while a trip is in the `building` stage. */
export type BuildSubState = 'unstarted' | 'draft' | 'waiting' | 'approved';

export interface TripDetailData {
  /** matches.id — used to build the handbook-builder and chat routes. */
  matchId: string;
  travelerName: string;
  /** 1–2 letter avatar initials, e.g. "OM". */
  initials: string;
  /** Avatar background color (any CSS color). Falls back to the host-avatar class. */
  avatarColor?: string;
  tripName: string;
  city: string;
  /** Human date range, e.g. "Jul 14–18". */
  dateRange: string;
  days: number;
  /** Nights — defaults to days − 1 when omitted. */
  nights?: number;
  /** Number of events planned in the handbook (shown for non-inquiry stages). */
  eventsPlanned?: number;
  stage: TripStage;
  buildState?: BuildSubState;
  /** Handbook completion 0–100 (building stage). */
  progress?: number;

  // ── What Alma learned — shown across every stage, not just inquiry ──
  party?: string;
  pace?: string;
  interests?: string[];
  dealbreakers?: string[];
  ask?: string;
  /** Relative submit time, e.g. "2h ago". */
  submittedLabel?: string;
  /** Alma's read on the inquiry (headline; optional score/signals/caution). */
  almaBriefing?: {
    headline: string;
    score?: number | null;
    signals?: string[];
    caution?: string | null;
  };

  // ── Active ──
  dayOfLabel?: string;
  lastUpdateLabel?: string;

  // ── Past ──
  completedLabel?: string;

  // ── Ready to invoice ──
  /** Where the money stands, e.g. "Invoice due · €51.26". */
  invoiceLabel?: string;
}

interface TripDetailPanelProps {
  /** The trip to show. Pass `null` to keep the panel closed/unmounted. */
  trip: TripDetailData | null;
  onClose: () => void;
  /** Inquiry → accept. Receives the matchId. */
  onAccept?: (matchId: string) => void;
  /** Inquiry → pass/decline. Receives the matchId. */
  onPass?: (matchId: string) => void;
  /**
   * Route to "preview as traveler" (the traveler-side handbook view).
   * When omitted, the preview button is hidden.
   */
  previewHref?: string;
  className?: string;
}

const STAGE_BADGE: Record<TripStage, { label: string; cls: string; dot: string }> = {
  inquiry:   { label: 'Inquiry',    cls: 'bg-app-card text-app-fg-2 border-app-border',                          dot: 'bg-app-muted' },
  building:  { label: 'Building',   cls: 'bg-[rgba(111,63,253,.13)] text-violet border-[rgba(111,63,253,.26)]',  dot: 'bg-violet' },
  confirmed: { label: 'Confirmed',  cls: 'bg-[rgba(255,153,59,.12)] text-gold border-[rgba(255,153,59,.24)]',    dot: 'bg-gold' },
  active:    { label: 'Active now', cls: 'bg-[rgba(158,201,138,.12)] text-success border-[rgba(158,201,138,.24)]', dot: 'bg-success' },
  invoice:   { label: 'Ready to invoice', cls: 'bg-[rgba(255,153,59,.12)] text-gold border-[rgba(255,153,59,.24)]', dot: 'bg-gold' },
  past:      { label: 'Completed',  cls: 'bg-app-card text-app-muted border-app-border',                         dot: 'bg-app-muted' },
};

const BUILD_LABEL: Record<BuildSubState, string> = {
  unstarted: 'Not started',
  draft: 'Draft',
  waiting: 'Waiting approval',
  approved: 'Approved',
};

function GlanceCard({ icon, label, value, sub }: { icon: string; label: string; value: string; sub?: string }) {
  return (
    <div className="flex-1 bg-app-card border border-app-border rounded-[10px] px-3.5 py-3">
      <div className="flex items-center gap-1.5 text-app-muted text-caption mb-1.5">
        <i className={cn('ti text-caption', icon)} />
        {label}
      </div>
      <div className="text-footnote text-app-fg font-medium">{value}</div>
      {sub && <div className="text-caption text-app-muted mt-0.5">{sub}</div>}
    </div>
  );
}

/**
 * TripDetailPanel — right-anchored slide-over that opens when a host taps a
 * trip card / list row / calendar bar on the dashboard. Its content and
 * primary action adapt to the trip's pipeline stage.
 *
 * Routes (built from matchId):
 *   • Handbook builder → /host/itinerary/:matchId   (ItineraryEditor)
 *   • Chat            → /host/chat/:matchId          (HostChat)
 *   • Preview         → previewHref prop (traveler handbook), optional
 *
 * Stage → primary action:
 *   inquiry   → Accept (onAccept) + Pass (onPass) + Message
 *   building  → Start / Continue / Review handbook (builder) + Chat
 *   confirmed → Edit handbook (builder) + Preview + Chat
 *   active    → Open live handbook (builder) + Preview + Chat
 *   past      → View handbook (builder) + Preview + Chat
 *
 * Responsive: full-width drawer on mobile, fixed 440px rail on ≥640px.
 * All action buttons are ≥44px tall for touch.
 */
export function TripDetailPanel({ trip, onClose, onAccept, onPass, previewHref, className }: TripDetailPanelProps) {
  const [visible, setVisible] = useState(false);
  const almaAvatar = useAlmaAvatar();

  // Animate in on mount; close on Escape.
  useEffect(() => {
    if (!trip) { setVisible(false); return; }
    const r = requestAnimationFrame(() => setVisible(true));
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { cancelAnimationFrame(r); window.removeEventListener('keydown', onKey); };
  }, [trip, onClose]);

  // Android hardware back button closes this panel instead of navigating
  // the route underneath it.
  useEffect(() => {
    if (!trip) return;
    return registerModalBackHandler(onClose);
  }, [trip, onClose]);

  if (!trip) return null;

  const {
    matchId, travelerName, initials, avatarColor, tripName, city, dateRange,
    days, nights, eventsPlanned, stage, buildState, progress = 0,
    party, pace, interests = [], dealbreakers = [], ask, submittedLabel, almaBriefing, dayOfLabel, lastUpdateLabel, completedLabel, invoiceLabel,
  } = trip;

  const builderHref = `/host/itinerary/${matchId}`;
  const chatHref = `/host/chat/${matchId}`;
  const nightsVal = nights ?? Math.max(days - 1, 1);
  const badge = STAGE_BADGE[stage];
  const statusLabel = stage === 'building' && buildState ? BUILD_LABEL[buildState] : stage === 'invoice' && invoiceLabel ? invoiceLabel : badge.label;

  const isInquiry = stage === 'inquiry';
  const isBuilding = stage === 'building';
  const isConfirmed = stage === 'confirmed';
  const isActive = stage === 'active';
  const isPast = stage === 'past' || stage === 'invoice';

  let primaryLabel = 'View handbook';
  let primaryIcon = 'ti-book';
  if (isBuilding) {
    primaryLabel = buildState === 'waiting' ? 'Review & resend' : buildState === 'unstarted' ? 'Start handbook' : 'Continue handbook';
    primaryIcon = 'ti-pencil';
  } else if (isConfirmed) { primaryLabel = 'Edit handbook'; primaryIcon = 'ti-pencil'; }
  else if (isActive) { primaryLabel = 'Open live handbook'; primaryIcon = 'ti-map-pin'; }

  return (
    <div className={cn('fixed inset-0 z-[60]', className)} role="dialog" aria-modal="true" aria-label={`${tripName} — ${travelerName}`}>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className={cn('absolute inset-0 bg-black/40 transition-opacity duration-200', visible ? 'opacity-100' : 'opacity-0')}
      />

      {/* Drawer */}
      <aside
        className={cn(
          'absolute top-0 right-0 h-full w-full sm:w-[440px] sm:max-w-[92vw]',
          'bg-app-bg border-l border-app-border shadow-[-14px_0_44px_rgba(0,0,0,.30)]',
          'flex flex-col transition-transform duration-300 ease-[cubic-bezier(.16,.8,.3,1)]',
          visible ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        {/* Header */}
        <div className="shrink-0 px-5 md:px-[22px] pt-5 pb-4 border-b border-app-border">
          <div className="flex items-start gap-3.5">
            <div
              className={cn('w-[52px] h-[52px] rounded-full flex items-center justify-center shrink-0', !avatarColor && 'avatar-host')}
              style={avatarColor ? { background: avatarColor } : undefined}
            >
              <span className="font-serif text-[length:calc(18px*var(--text-scale))] text-cream leading-none">{initials}</span>
            </div>
            <div className="flex-1 min-w-0 flex flex-col gap-1.5">
              <span className="text-subhead font-semibold text-app-fg leading-tight">{travelerName}</span>
              <span className={cn('self-start inline-flex items-center gap-1.5 rounded-pill border px-[11px] py-1 text-caption font-medium whitespace-nowrap', badge.cls)}>
                <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', badge.dot)} />
                {statusLabel}
              </span>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-app-muted hover:bg-app-card hover:text-app-fg transition-colors sk-hit"
            >
              <i className="ti ti-x text-[length:calc(17px*var(--text-scale))]" />
            </button>
          </div>
          <h2 className="font-serif text-[length:calc(21px*var(--text-scale))] text-app-fg leading-[1.25] mt-4 text-pretty">{tripName}</h2>
          <div className="flex items-center gap-1.5 text-app-muted text-caption mt-[7px]">
            <i className="ti ti-map-pin text-[length:calc(12px*var(--text-scale))]" />
            {city} · {dateRange}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto px-5 md:px-[22px] py-5 flex flex-col gap-[22px]">
          {/* At a glance */}
          <div className="flex gap-2.5 flex-wrap">
            <GlanceCard icon="ti-calendar-event" label="Dates" value={dateRange} sub={`${days} days · ${nightsVal} nights`} />
            {!isInquiry && eventsPlanned != null && (
              <GlanceCard icon="ti-route" label="Itinerary" value={`${eventsPlanned} events planned`} />
            )}
            {party && <GlanceCard icon="ti-users" label="Party" value={party} />}
            {pace && <GlanceCard icon="ti-gauge" label="Pace" value={pace} />}
          </div>

          {/* What Alma learned from the traveler — visible for the whole trip,
              not just the accept/pass decision, since the host needs this
              while building the handbook too. */}
          {almaBriefing && (
            <AlmaBriefing
              headline={almaBriefing.headline}
              score={isInquiry ? almaBriefing.score : undefined}
              signals={isInquiry ? almaBriefing.signals : undefined}
              caution={isInquiry ? almaBriefing.caution : undefined}
              avatarUrl={almaAvatar ?? undefined}
            />
          )}

          {ask && (
            <div>
              <div className="text-app-muted text-caption font-medium mb-2.5">What they're hoping for</div>
              <div className="bg-[rgba(255,153,59,.055)] border border-[rgba(255,153,59,.18)] rounded-xl px-[17px] py-[15px]">
                <p className="font-serif text-subhead leading-[1.6] text-app-fg text-pretty">"{ask}"</p>
              </div>
              {interests.length > 0 && (
                <div className="flex flex-wrap gap-[7px] mt-3">
                  {interests.map((t) => (
                    <span key={t} className="text-caption font-medium text-app-fg-2 bg-app-card border border-app-border rounded-pill px-[11px] py-1 whitespace-nowrap">
                      {t}
                    </span>
                  ))}
                </div>
              )}
              {dealbreakers.length > 0 && (
                <div className="flex flex-wrap gap-[7px] mt-2">
                  {dealbreakers.map((t) => (
                    <span key={t} className="text-caption font-medium text-crimson bg-crimson/8 border border-crimson/20 rounded-pill px-[11px] py-1 whitespace-nowrap">
                      <i className="ti ti-ban text-[length:calc(10px*var(--text-scale))] mr-1" />{t}
                    </span>
                  ))}
                </div>
              )}
              {isInquiry && submittedLabel && (
                <div className="flex items-center gap-1.5 mt-3 text-app-muted text-caption">
                  <i className="ti ti-clock text-[length:calc(12px*var(--text-scale))]" />
                  Submitted {submittedLabel}
                </div>
              )}
            </div>
          )}

          {/* Building: handbook progress */}
          {isBuilding && (
            <div>
              <div className="flex items-center justify-between mb-[9px]">
                <div className="text-app-muted text-caption font-medium">Handbook</div>
                <div className="text-app-fg-2 text-caption">{progress}% complete</div>
              </div>
              <div className="h-1.5 bg-app-border rounded-[3px] overflow-hidden">
                <div className="h-full bg-violet rounded-[3px] transition-[width] duration-300" style={{ width: `${progress}%` }} />
              </div>
              {buildState === 'waiting' && (
                <div className="flex items-center gap-[7px] mt-3 bg-app-card border border-app-border rounded-[10px] px-3.5 py-[11px]">
                  <i className="ti ti-clock-hour-4 text-[length:calc(14px*var(--text-scale))] text-app-muted shrink-0" />
                  <span className="text-caption text-app-fg-2 text-pretty">Sent to {travelerName} — waiting on their approval.</span>
                </div>
              )}
            </div>
          )}

          {/* Confirmed / Active: approved note */}
          {(isConfirmed || isActive) && (
            <div className="flex items-center gap-2.5 bg-[rgba(158,201,138,.10)] border border-[rgba(158,201,138,.26)] rounded-[10px] px-[15px] py-3">
              <i className="ti ti-circle-check text-[length:calc(17px*var(--text-scale))] text-[--success-fg] shrink-0" />
              <span className="text-footnote text-[--success-fg] font-medium">Handbook approved by {travelerName}</span>
            </div>
          )}

          {/* Active: live banner */}
          {isActive && dayOfLabel && (
            <div className="flex items-center gap-2.5 bg-app-card border border-app-border rounded-[10px] px-[15px] py-3">
              <span className="w-2 h-2 rounded-full bg-success shrink-0" />
              <span className="text-footnote font-medium text-[--success-fg]">{dayOfLabel}</span>
              {lastUpdateLabel && <span className="ml-auto text-caption text-app-muted">Updated {lastUpdateLabel}</span>}
            </div>
          )}

          {/* Past: completed */}
          {isPast && completedLabel && (
            <div className="flex items-center gap-2.5 bg-app-card border border-app-border rounded-[10px] px-[15px] py-3">
              <i className="ti ti-flag-check text-[length:calc(16px*var(--text-scale))] text-app-muted shrink-0" />
              <span className="text-footnote text-app-fg-2">Completed {completedLabel}</span>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="shrink-0 border-t border-app-border px-5 md:px-[22px] py-4 flex flex-col gap-2.5">
          {isInquiry ? (
            <>
              <div className="flex gap-2.5">
                <button
                  onClick={() => onAccept?.(matchId)}
                  className="flex-1 min-h-[44px] inline-flex items-center justify-center gap-1.5 rounded-pill text-footnote font-semibold bg-[rgba(158,201,138,.13)] text-[--success-fg] border border-[rgba(158,201,138,.34)] hover:bg-[rgba(158,201,138,.22)] transition-colors"
                >
                  <i className="ti ti-check text-[length:calc(15px*var(--text-scale))]" />
                  Accept inquiry
                </button>
                <button
                  onClick={() => onPass?.(matchId)}
                  className="shrink-0 min-h-[44px] px-4 inline-flex items-center justify-center rounded-pill text-footnote font-semibold bg-transparent text-app-fg border border-app-muted hover:border-app-fg transition-colors"
                >
                  Pass
                </button>
              </div>
              <Link
                to={chatHref}
                className="w-full min-h-[44px] inline-flex items-center justify-center gap-1.5 rounded-pill text-footnote font-semibold bg-transparent text-app-fg border border-app-muted hover:border-app-fg transition-colors"
              >
                <i className="ti ti-message-circle text-[length:calc(15px*var(--text-scale))]" />
                Message {travelerName}
              </Link>
            </>
          ) : (
            <>
              <div className="flex gap-2.5">
                <Link
                  to={builderHref}
                  className="flex-1 min-h-[44px] inline-flex items-center justify-center gap-1.5 rounded-pill text-footnote font-semibold bg-app-fg text-app-bg border border-app-fg hover:opacity-[.86] transition-opacity"
                >
                  <i className={cn('ti text-subhead', primaryIcon)} />
                  {primaryLabel}
                </Link>
                <Link
                  to={chatHref}
                  aria-label="Chat"
                  className="shrink-0 w-[46px] min-h-[44px] inline-flex items-center justify-center rounded-pill bg-transparent text-app-fg border border-app-muted hover:border-app-fg transition-colors"
                >
                  <i className="ti ti-message-circle text-[length:calc(16px*var(--text-scale))]" />
                </Link>
              </div>
              {previewHref && (isConfirmed || isActive || isPast) && (
                <Link
                  to={previewHref}
                  className="w-full min-h-[44px] inline-flex items-center justify-center gap-1.5 rounded-pill text-footnote font-semibold bg-transparent text-app-fg border border-app-muted hover:border-app-fg transition-colors"
                >
                  <i className="ti ti-eye text-[length:calc(15px*var(--text-scale))]" />
                  Preview as traveler
                </Link>
              )}
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

export default TripDetailPanel;
